<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  FhButton,
  FhDropdownMenu,
  FhDropdownSection,
  FhDropdownSingleSelectItem,
  FhMultiselect,
} from '@fareharbor-com/beacon-vue';
import {
  clock,
  creditCard,
  seatingGrid,
} from '@fareharbor-com/beacon-vue/icons';
import { storeToRefs } from 'pinia';
import { SORT_BY_OPTIONS, SortByOption } from '@/common/constants/items';
import { OFFERED_SINCE_SELECT_OPTIONS } from '@/components/itemList/filters';
import { OfferedSinceOptionVariant } from '@/components/itemList/types';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';
import { useCurrenciesStore } from '@/stores/currencies';
import { useItemFiltersStore } from '@/stores/itemFilters';
import { useTagsStore } from '@/stores/tags';
import type { FiltersChangedEventData } from './types';

const tagsStore = useTagsStore();
const currenciesStore = useCurrenciesStore();
const itemFiltersStore = useItemFiltersStore();

const { tags: selectedTagSet } = storeToRefs(itemFiltersStore);
const { isReady: currReady } = storeToRefs(currenciesStore);
const { isLoading: tagsLoading, hasLoadedOnce: tagsHasLoadedOnce } =
  storeToRefs(tagsStore);
const tagsReady = computed(() => tagsHasLoadedOnce.value && !tagsLoading.value);

const props = defineProps<{
  currentSortBy: number;
}>();

const emit = defineEmits<{
  filtersChanged: [FiltersChangedEventData];
  sortByChanged: [SortByOption];
}>();

// Ref to control the selected offered since option, always initialized with a value
const selectedOfferedSinceRef = ref<number[]>([
  itemFiltersStore.offeredSinceVariant,
]);

const offeredSinceOptions = computed(() =>
  OFFERED_SINCE_SELECT_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
    disabled: false, // No options are disabled
  })),
);

const itemTypeOptions = computed(() =>
  tagsStore.tags.map((tag) => ({
    value: tag.shortname,
    label: `${tag.name} (${tag.relatedItemsCount})`,
    disabled: false,
  })),
);

const currencyOptions = computed(() =>
  currenciesStore.currencies.map((cur) => ({
    value: cur.code,
    label: `${cur.code.toUpperCase()} (${cur.relatedItemsCount})`,
    disabled: false,
  })),
);

const selectedOfferedSince = computed<number[]>({
  get: () => selectedOfferedSinceRef.value,
  set: (values) => {
    // If trying to deselect all options, select the "No preference" option
    if (values.length === 0) {
      const noPreferenceOption = [OfferedSinceOptionVariant.NO_PREFERENCE];
      selectedOfferedSinceRef.value = noPreferenceOption;
      itemFiltersStore.offeredSinceVariant =
        OfferedSinceOptionVariant.NO_PREFERENCE;
      emit('filtersChanged', { shouldConfirm: false });
      return;
    }

    // If trying to select multiple, keep only the latest selection
    const newSelection =
      values.length > 1 ? [values[values.length - 1]] : values;
    selectedOfferedSinceRef.value = newSelection;

    const [selectedValue] = newSelection;
    itemFiltersStore.offeredSinceVariant = selectedValue;
    emit('filtersChanged', { shouldConfirm: false });
  },
});

const selectedItemTypes = computed<string[]>({
  get: () => Array.from(selectedTagSet.value),
  set: (vals) => {
    const curr = selectedTagSet.value;
    if (curr.size === vals.length && vals.every((v) => curr.has(v))) return;
    selectedTagSet.value = new Set(vals);
    emit('filtersChanged', { shouldConfirm: true });
  },
});

const selectedCurrencies = computed<string[]>({
  get: () => Array.from(itemFiltersStore.currencies),
  set: (vals) => {
    const curr = itemFiltersStore.currencies;
    if (curr.size === vals.length && vals.every((v) => curr.has(v))) return;
    itemFiltersStore.currencies = new Set(vals);
    emit('filtersChanged', { shouldConfirm: true });
  },
});

function handleSortByChange(newOption: SortByOption) {
  emit('sortByChanged', newOption);
}
</script>

<template>
  <div class="flex justify-between items-start w-full">
    <BaseFlexbox
      direction="column"
      alignItems="start"
      gap="4"
      class="w-full md:flex-row md:flex-wrap lg:flex-nowrap"
      data-test-id="item-filters"
    >
      <div class="w-full xs:w-1/2 md:flex-1">
        <FhMultiselect
          v-model="selectedOfferedSince"
          :options="offeredSinceOptions"
          :selectAll="false"
          placeholder="Offered Since"
          data-test-id="offered-since-filter"
          class="w-full"
          :prefixIcon="clock"
        />
      </div>

      <div class="w-full xs:w-1/2 md:flex-1">
        <!-- WORKAROUND ↓  Beacon's <FhMultiselect> throws `isOpen` null-ref
        when its :disabled state flips at runtime. We therefore mount
        it only after tags are loaded.  Delete this guard once the bug
        is fixed upstream. -->
        <FhMultiselect
          v-if="tagsReady"
          v-model="selectedItemTypes"
          :options="itemTypeOptions"
          :selectAll="false"
          placeholder="Item Type"
          data-test-id="item-type-filter"
          class="w-full"
          :prefixIcon="seatingGrid"
        />
        <!-- lightweight placeholder while tags load -->
        <FhMultiselect
          v-else
          class="w-full px-3 py-2 border rounded bg-gray-100"
          disabled
          value="Loading…"
        />
      </div>

      <div class="w-full xs:w-1/2 md:flex-1">
        <FhMultiselect
          v-model="selectedCurrencies"
          :options="currencyOptions"
          :selectAll="false"
          :disabled="!currReady"
          placeholder="Currency"
          data-test-id="currency-filter-section"
          class="w-full"
          :prefixIcon="creditCard"
        />
      </div>
      <div class="w-full xs:w-1/2 md:flex-1" data-test-id="current-sort-by">
        <FhDropdownMenu class="w-full">
          <FhButton variant="secondary" size="sm" class="w-full">
            Sorted by
            {{
              SORT_BY_OPTIONS.find((opt) => opt.value === props.currentSortBy)
                ?.label
            }}
          </FhButton>
          <template #sections>
            <FhDropdownSection
              name="sort"
              :modelValue="props.currentSortBy"
              type="single-select"
              @update:modelValue="handleSortByChange"
            >
              <FhDropdownSingleSelectItem
                v-for="option in SORT_BY_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </FhDropdownSingleSelectItem>
            </FhDropdownSection>
          </template>
        </FhDropdownMenu>
      </div>
    </BaseFlexbox>
  </div>
</template>
